import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, ArrowUpDown, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatDateLocale, dateInputLang } from '../../utils/dateFormat';

// SALES-CRM-SIMPLIFICATION / Patient list UX — presentation only.
// Active/History, current version and actionability come from the V3.2 server projection
// (`offer_groups.group_active`, `offer_groups.latest_id`, row `is_current`, row `quote_actionable`).
// Filtering and sorting only decide WHICH existing rows are shown and in what order.

export const splitOfferScopes = (quotes, groups) => {
  // MULTIPLE-ACTIONABLE-ALTERNATIVES — Active = every individually actionable offer
  // (per-offer `quote_actionable` from the backend projection), not one-per-group.
  const hasFlag = (quotes || []).some(q => typeof q.quote_actionable === 'boolean');
  if (hasFlag) {
    return {
      active: (quotes || []).filter(q => q.quote_actionable === true),
      history: (quotes || []).filter(q => q.quote_actionable !== true),
    };
  }
  const activeLatest = new Set((groups || []).filter(g => g.group_active).map(g => g.latest_id));
  return {
    active: quotes.filter(q => activeLatest.has(q.id)),
    history: quotes.filter(q => !activeLatest.has(q.id)),
  };
};

export const offerStatusCode = (q) => (q.is_current === false ? 'EARLIER_VERSION' : q.status);
const clinicName = (q) => q.clinic_name || q.clinic_info?.clinic_name || '';
const offerDate = (q) => q.sent_at || q.created_at || '';
const offerTotal = (q) => (Number.isFinite(q.financial_summary?.total) ? q.financial_summary.total : null);

const STATUS_STYLE = {
  SENT: 'bg-blue-100 text-blue-700', VIEWED: 'bg-amber-100 text-amber-700', ACCEPTED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-slate-100 text-slate-600', EXPIRED: 'bg-red-100 text-red-600', EARLIER_VERSION: 'bg-gray-100 text-gray-500',
};

export const OfferStatusBadge = ({ quote }) => {
  const { t } = useTranslation();
  const code = offerStatusCode(quote);
  return (
    <Badge className={`text-xs ${STATUS_STYLE[code] || 'bg-gray-100 text-gray-700'}`} data-testid={`offer-status-${quote.id}`}>
      {t(`patient.offers.status.${code}`)}
    </Badge>
  );
};

const SortHead = ({ col, label, sort, onSort, align = 'left', testId, colTestId }) => {
  const active = sort.key === col;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 text-xs font-semibold text-gray-600 uppercase text-${align}`} data-testid={colTestId}>
      <button type="button" onClick={() => onSort(col)} className={`inline-flex items-center gap-1 hover:text-gray-900 ${active ? 'text-gray-900' : ''}`} data-testid={testId} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        {label} <Icon className="h-3 w-3" />
      </button>
    </th>
  );
};

const FilterSelect = ({ value, onChange, options, allLabel, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs border rounded-md px-2 bg-white" data-testid={testId}>
    <option value="">{allLabel}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const PatientOfferTable = ({ quotes, groups, showHistory, onShowHistory, renderActions, showValidUntil = false, showVersion = false, onHistoryLink }) => {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ clinic: '', treatment: '', status: '', from: '', to: '' });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const scopes = useMemo(() => splitOfferScopes(quotes, groups), [quotes, groups]);
  const scopeRows = showHistory ? scopes.history : scopes.active;
  const groupById = useMemo(() => Object.fromEntries((groups || []).map(g => [g.lead_id, g])), [groups]);

  const options = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
    return {
      clinic: uniq(scopeRows.map(clinicName)).map(v => ({ value: v, label: v })),
      treatment: uniq(scopeRows.map(q => q.treatment_name)).map(v => ({ value: v, label: v })),
      status: uniq(scopeRows.map(offerStatusCode)).map(v => ({ value: v, label: t(`patient.offers.status.${v}`) })),
    };
  }, [scopeRows, t]);

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const filtered = scopeRows.filter(q =>
      (!filters.clinic || clinicName(q) === filters.clinic) &&
      (!filters.treatment || q.treatment_name === filters.treatment) &&
      (!filters.status || offerStatusCode(q) === filters.status) &&
      (!filters.from || offerDate(q).slice(0, 10) >= filters.from) &&
      (!filters.to || offerDate(q).slice(0, 10) <= filters.to) &&
      (!s || clinicName(q).toLowerCase().includes(s) || (q.treatment_name || '').toLowerCase().includes(s)));
    const val = {
      clinic: clinicName, treatment: (q) => q.treatment_name || '', price: (q) => offerTotal(q) ?? -1,
      date: offerDate, status: (q) => t(`patient.offers.status.${offerStatusCode(q)}`),
    }[sort.key];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const x = val(a), y = val(b);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }, [scopeRows, filters, sort, t, search]);

  const onSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'date' ? 'desc' : 'asc' }));
  const setF = (k) => (v) => setFilters(f => ({ ...f, [k]: v }));
  const filtered = rows.length !== scopeRows.length;
  // A2 — contextual empty state driven ONLY by data already on this surface (no new query,
  // no request-lifecycle duplication). scopeRows empty ⇒ nothing in this scope at all;
  // rows empty while scopeRows non-empty ⇒ filters/search excluded them.
  const emptyKey = scopeRows.length === 0
    ? (!showHistory && scopes.history.length > 0
        ? 'patient.offers.emptyAwaitingRevision'   // active none, but prior offers exist → likely awaiting revision
        : 'patient.offers.emptyActiveNone')        // truly nothing yet (or empty history scope)
    : 'patient.offers.filters.noRows';             // filters/search excluded existing offers

  return (
    <div data-testid="patient-offer-table">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex gap-1" data-testid="offers-scope-toggle">
          <Button size="sm" variant={showHistory ? 'ghost' : 'default'} onClick={() => onShowHistory(false)} data-testid="offers-scope-active">
            {t('patient.offers.scopeActive')} ({scopes.active.length})
          </Button>
          <Button size="sm" variant={showHistory ? 'default' : 'ghost'} onClick={() => onShowHistory(true)} data-testid="offers-scope-history">
            {t('patient.offers.scopeHistory')} ({scopes.history.length})
          </Button>
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="offers-filters">
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('patient.offers.filters.searchPlaceholder')} className="h-8 text-xs border rounded-md px-2 min-w-[150px]" data-testid="offers-search-input" />
          <FilterSelect value={filters.clinic} onChange={setF('clinic')} options={options.clinic} allLabel={t('patient.offers.filters.allClinics')} testId="offers-filter-clinic" />
          <FilterSelect value={filters.treatment} onChange={setF('treatment')} options={options.treatment} allLabel={t('patient.offers.filters.allTreatments')} testId="offers-filter-treatment" />
          <FilterSelect value={filters.status} onChange={setF('status')} options={options.status} allLabel={t('patient.offers.filters.allStatuses')} testId="offers-filter-status" />
          <input type="date" lang={dateInputLang(i18n.language)} value={filters.from} onChange={(e) => setF('from')(e.target.value)} className="h-8 text-xs border rounded-md px-2" aria-label={t('patient.offers.filters.from')} data-testid="offers-filter-from" />
          <input type="date" lang={dateInputLang(i18n.language)} value={filters.to} onChange={(e) => setF('to')(e.target.value)} className="h-8 text-xs border rounded-md px-2" aria-label={t('patient.offers.filters.to')} data-testid="offers-filter-to" />
          {(filtered || search) && (
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setFilters({ clinic: '', treatment: '', status: '', from: '', to: '' }); setSearch(''); }} data-testid="offers-filter-clear">
              <X className="h-3 w-3 mr-1" />{t('patient.offers.filters.clear')}
            </Button>
          )}
        </div>
      </div>
      {filtered && (
        <p className="text-xs text-slate-500 mb-2" data-testid="offers-filter-summary">{t('patient.offers.filters.showing', { shown: rows.length, total: scopeRows.length })}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortHead col="clinic" label={t('patient.dashboard.colClinic')} sort={sort} onSort={onSort} testId="offers-sort-clinic" />
              <SortHead col="treatment" label={t('patient.dashboard.colOfferedTreatment')} sort={sort} onSort={onSort} testId="offers-sort-treatment" colTestId="offers-col-offered-treatment" />
              <SortHead col="price" label={t('patient.offers.cols.price')} sort={sort} onSort={onSort} align="right" testId="offers-sort-price" />
              <SortHead col="date" label={t('patient.offers.cols.sentDate')} sort={sort} onSort={onSort} align="center" testId="offers-sort-date" />
              <SortHead col="status" label={t('patient.dashboard.colStatus')} sort={sort} onSort={onSort} align="center" testId="offers-sort-status" />
              {showValidUntil && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t('patient.offers.cols.validUntil')}</th>}
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t('patient.dashboard.colActions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y" data-testid="offers-table-body">
            {rows.length === 0 && (
              <tr><td colSpan={showValidUntil ? 7 : 6} className="px-4 py-8 text-center text-sm text-gray-500" data-testid="offers-empty-scope">{t(emptyKey)}</td></tr>
            )}
            {rows.map(q => {
              const fs = q.financial_summary || {};
              const total = offerTotal(q);
              const group = groupById[q.lead_id];
              const earlier = (group?.versions?.length || 1) - 1;
              return (
                <tr key={q.id} className={`hover:bg-gray-50 ${q.is_current === false ? 'opacity-70' : ''}`} data-testid={`offer-row-${q.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-sm">{clinicName(q) || t('patient.dashboard.colClinic')}</p>
                    {q.clinic_info?.country && <p className="text-xs text-gray-500">{q.clinic_info.country}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm" data-testid={`offer-treatment-${q.id}`}>
                    {q.treatment_name || t('patient.dashboard.treatmentPackage')}
                    {showVersion && (
                      <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`offer-version-${q.id}`}>
                        {t('patient.offers.versionLabel', { n: q.version_number || 1 })}
                        {q.is_current !== false && earlier > 0 && !showHistory && (
                          <button type="button" className="ml-1 underline text-purple-600" onClick={() => (onHistoryLink || onShowHistory)(true)} data-testid={`offer-history-link-${q.id}`}>
                            · {t('patient.offers.versionsCount', { count: earlier })}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-600" data-testid={`offer-row-total-${q.id}`}>
                    {total != null ? `${fs.currency || q.currency || ''} ${total.toLocaleString()}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500" data-testid={`offer-sent-date-${q.id}`}>{offerDate(q) ? formatDateLocale(offerDate(q), i18n.language) : '-'}</td>
                  <td className="px-4 py-3 text-center"><OfferStatusBadge quote={q} /></td>
                  {showValidUntil && (
                    <td className="px-4 py-3 text-center text-xs text-gray-500">{q.valid_until ? formatDateLocale(q.valid_until, i18n.language) : '-'}</td>
                  )}
                  <td className="px-4 py-3"><div className="flex items-center justify-center gap-1">{renderActions(q)}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
